import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, TrendingUp, CheckCircle2, AlertTriangle, Clock, ArrowRight, Users, Activity, Building2, Award, Layers, Package, Bell, Wallet } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatINRCompact } from "@/utils/formatINR";
import ApqpDashboardCard from "@/components/ApqpDashboardCard";
import AwardedOutlook5Y from "@/components/AwardedOutlook5Y";

const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700",
  n1: "bg-sky-100 text-sky-700", n2: "bg-sky-100 text-sky-700",
  n3: "bg-sky-100 text-sky-700", n4: "bg-sky-100 text-sky-700", n5: "bg-sky-100 text-sky-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [alertCounts, setAlertCounts] = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const hideMargin = user?.role === "npd_engineer";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    // R30 — fetch alert counts for the top-of-dashboard urgency banner
    api.get("/alerts/me").then((r) => setAlertCounts(r.data.counts || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin && unitFilter) params.unit_id = unitFilter;
      const r = await api.get("/npd/_/dashboard", { params });
      setData(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitFilter]);

  const kpi = data?.kpis || {};
  const recent = data?.recent || [];
  const top = data?.top_customers || [];
  const breakdown = data?.units_breakdown || [];
  // Cross-unit comparison only makes sense when viewing all units. With one unit picked,
  // the "by unit" views collapse so the whole dashboard reflects just that unit.
  const byUnit = !unitFilter && breakdown.length > 1;
  // The per-unit table only shows for the all-units view; when it's hidden the row below
  // becomes two equal cards (Recent NPDs + Top Customers) instead of leaving a gap.
  const showUnitTable = isAdmin && !unitFilter && breakdown.length > 0;
  const asmKpi = data?.assembly_kpis || {};
  const recentAsm = data?.recent_assemblies || [];
  const asmBreakdown = data?.assembly_units_breakdown || [];
  const rfqKpi = data?.rfq_kpis || {};
  const outlook5y = data?.business_outlook_5y || {};
  const winRateTrend = data?.win_rate_trend || [];
  const activeLabel = unitFilter
    ? (units.find((u) => u.unit_id === unitFilter)?.unit_code || "Unit")
    : "All Units";

  // Maintenance-style scope pills: Indian financial year (Apr–Mar) + current month.
  const _now = new Date();
  const _fyStart = _now.getMonth() >= 3 ? _now.getFullYear() : _now.getFullYear() - 1;
  const fyLabel = `FY ${_fyStart}-${String((_fyStart + 1) % 100).padStart(2, "0")}`;
  const monthLabel = `${_now.toLocaleString("en-US", { month: "short" }).toUpperCase()}-${String(_now.getFullYear() % 100).padStart(2, "0")}`;

  // Award conversion = confirmed share of everything currently costed (awarded + still open).
  const quantumBase = (kpi.awarded_business_quantum || 0) + (kpi.potential_business_quantum || 0);
  const awardPct = quantumBase > 0 ? (kpi.awarded_business_quantum / quantumBase) * 100 : 0;

  return (
    <div className="p-2.5 lg:p-3 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="bg-white border border-slate-200 border-l-4 border-l-orange-500 rounded-xl px-4 py-3 mb-3 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm">
        <div className="min-w-0">
          <h1 className="text-base font-bold text-slate-900">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {hideMargin ? "Viewing NPDs assigned to you." : isAdmin ? `Viewing: ${activeLabel}` : "Overview of the NPD pipeline."}
          </p>
        </div>
        <div className="sm:ml-auto flex items-center gap-1.5 flex-wrap">
          <Pill>{fyLabel}</Pill>
          <Pill active>{monthLabel}</Pill>
          {isAdmin && (
            <select
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs bg-white"
              data-testid="dashboard-unit-filter"
            >
              <option value="">All Units (collective)</option>
              {units.map((u) => (
                <option key={u.unit_id} value={u.unit_id}>
                  {u.unit_code} — {u.unit_name}
                </option>
              ))}
            </select>
          )}
          <Link
            to="/npd/new"
            className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors shadow-sm"
            data-testid="dashboard-new-npd"
          >
            <FileText size={14} /> New NPD Costing
          </Link>
        </div>
      </div>

      {/* R30 — Due-date urgency banner */}
      {(alertCounts.critical > 0 || alertCounts.high > 0) && (
        <div
          className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
          data-testid="dashboard-alerts-banner"
        >
          <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Bell size={14} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-red-900">
              You have urgent due-date alerts
            </div>
            <div className="text-[11px] text-red-800/80 mt-0.5">
              {alertCounts.critical > 0 && <span><strong>{alertCounts.critical}</strong> overdue · </span>}
              {alertCounts.high > 0 && <span><strong>{alertCounts.high}</strong> due in ≤3 days · </span>}
              {alertCounts.medium > 0 && <span>{alertCounts.medium} due in ≤7d</span>}
            </div>
          </div>
          <span className="text-[11px] text-red-700 font-medium hidden sm:block">Open the Alerts bell in the sidebar →</span>
        </div>
      )}

      {isAdmin && breakdown.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 mb-3 flex items-center gap-2 overflow-x-auto" data-testid="dashboard-unit-strip">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider whitespace-nowrap mr-1">Quick view:</span>
          {breakdown.map((u) => {
            const active = unitFilter === u.unit_id;
            return (
              <button
                key={u.unit_id}
                type="button"
                onClick={() => setUnitFilter(active ? "" : u.unit_id)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] transition whitespace-nowrap ${
                  active
                    ? "bg-orange-50 border-orange-400 text-orange-900 ring-1 ring-orange-200"
                    : "bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-700"
                }`}
                data-testid={`strip-${u.unit_code}`}
                title={u.unit_name}
              >
                <span className="font-mono font-bold">{u.unit_code}</span>
                <span className="text-slate-300">·</span>
                <span className="font-mono">{u.total}</span>
                <span className="text-slate-400 text-[10px]">NPD{u.total !== 1 ? "s" : ""}</span>
                {(u.awarded_business_quantum || 0) > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="text-amber-700 font-semibold flex items-center gap-0.5" title={formatINR(u.awarded_business_quantum)}>
                      <Award size={11} /> {formatINRCompact(u.awarded_business_quantum)}
                    </span>
                  </>
                )}
                {u.red_flags > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="text-red-600 font-semibold flex items-center gap-0.5">
                      <AlertTriangle size={11} /> {u.red_flags}
                    </span>
                  </>
                )}
              </button>
            );
          })}
          {unitFilter && (
            <button
              type="button"
              onClick={() => setUnitFilter("")}
              className="ml-auto text-[11px] text-slate-500 hover:text-slate-700 underline whitespace-nowrap"
              data-testid="strip-clear"
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* ---- Compact KPI strip (one dense row, maintenance-style) ---- */}
      <SectionLabel icon={Activity} right={activeLabel}>Pipeline snapshot</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2.5 mb-3" data-testid="dashboard-module-summary">
        <TintTile icon={FileText} tint="orange" label={hideMargin ? "My Costings" : "Total Costings"} value={kpi.total} loading={loading}
          caption="All live costings" to={`/npd?${unitFilter ? `unit_id=${unitFilter}` : ""}`} />
        <TintTile icon={Clock} tint="amber" label="Draft Costings" value={kpi.draft} loading={loading}
          caption="Not yet quoted" to={`/npd?status=draft${unitFilter ? `&unit_id=${unitFilter}` : ""}`} />
        <TintTile icon={Activity} tint="sky" label="In-Negotiation" value={kpi.in_negotiation} loading={loading}
          caption="Rounds N1–N5" to={`/npd?status=in_negotiation${unitFilter ? `&unit_id=${unitFilter}` : ""}`} />
        <TintTile icon={CheckCircle2} tint="emerald" label="Approved Costings" value={kpi.approved} loading={loading}
          caption="Cleared for export" to={`/npd?approved=yes${unitFilter ? `&unit_id=${unitFilter}` : ""}`} />
        <TintTile icon={Package} tint="violet" label="RFQ Register" value={(data?.module_counts || {}).rfq} loading={loading}
          caption="Customer RFQs logged" to="/rfqs" />
        <TintTile icon={Layers} tint="rose" label="APQP Projects" value={(data?.module_counts || {}).apqp} loading={loading}
          caption="Won → APQP plans" to="/apqp" />
      </div>

      {/* ---- Maintenance-style panel row: status donut · by-unit bars · business ---- */}
      {!hideMargin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2.5 mb-3" data-testid="dashboard-gauges">
          <Panel icon={Activity} title="Costing status" right={<span className="text-[10px] font-medium text-slate-400">{activeLabel}</span>}>
            <div className="flex items-center gap-3">
              <MiniDonut pct={kpi.total ? (kpi.approved / kpi.total) * 100 : 0} color="#10b981"
                label={`${kpi.total ? Math.round((kpi.approved / kpi.total) * 100) : 0}%`} sub="approved" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <StatusBar color="bg-slate-400"   label="Draft"       value={kpi.draft || 0}          total={kpi.total || 1} />
                <StatusBar color="bg-sky-500"      label="Negotiation" value={kpi.in_negotiation || 0} total={kpi.total || 1} />
                <StatusBar color="bg-emerald-500"  label="Approved"    value={kpi.approved || 0}       total={kpi.total || 1} />
              </div>
            </div>
          </Panel>

          <Panel icon={Building2} title={byUnit ? "Costings by unit" : "Costings by stage"}
            right={byUnit ? <span className="text-[10px] font-medium text-slate-400">{breakdown.length} units</span> : null}>
            <div className="space-y-1.5">
              {(byUnit
                ? breakdown.map((u) => ({ label: u.unit_code, value: u.total }))
                : [
                    { label: "Draft", value: kpi.draft || 0 },
                    { label: "Negotiation", value: kpi.in_negotiation || 0 },
                    { label: "Approved", value: kpi.approved || 0 },
                  ]
              ).map((d, i, arr) => (
                <BarRow key={d.label} label={d.label} value={d.value}
                  pct={(d.value / Math.max(1, ...arr.map((x) => x.value))) * 100} />
              ))}
            </div>
          </Panel>

          <Panel icon={Wallet} title="Business snapshot">
            <div className="space-y-2.5">
              <MiniStat label="Pipeline value" sub={`across ${(kpi.total ?? 0).toLocaleString("en-IN")} parts`}
                value={formatINRCompact(kpi.pipeline_value)} title={formatINR(kpi.pipeline_value)} />
              <MiniStat label="Award conversion" color="text-amber-600"
                sub={`${formatINRCompact(kpi.awarded_business_quantum)} won · ${formatINRCompact(kpi.potential_business_quantum)} open`}
                value={`${awardPct.toFixed(1)}%`} />
              <MiniStat label="RFQ win rate" color="text-violet-600"
                sub={`${rfqKpi.won || 0}W · ${rfqKpi.lost || 0}L · ${rfqKpi.in_flight || 0} live`}
                value={rfqKpi.win_rate_pct == null ? "—" : `${rfqKpi.win_rate_pct}%`} />
            </div>
          </Panel>
        </div>
      )}

      {/* ---- Awarded projects · 5-year business outlook ---- */}
      {!hideMargin && (
        <AwardedOutlook5Y outlook={outlook5y} scopeLabel={activeLabel} unitFilter={unitFilter} />
      )}

      {hasPermission("apqp.view") && <ApqpDashboardCard />}

      {!hideMargin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-3">
          <div className="bg-gradient-to-br from-indigo-50/70 to-white border border-indigo-100 rounded-xl p-3 lg:col-span-2" data-testid="business-outlook-5y">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-sm text-slate-900">Total 5-Year Outlook · {activeLabel}</h3>
                <p className="text-xs text-slate-500">Awarded <em>and</em> not-yet-awarded: sum of TPV across live NPDs + Assemblies, each capped at min(project life, 5 years). For strategic capex planning.</p>
              </div>
              <TrendingUp size={18} className="text-slate-400" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total (5y capped)</div>
                <div className="text-xl font-bold text-slate-900 mt-0.5" title={formatINR(outlook5y.total || 0)}>{formatINRCompact(outlook5y.total || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">NPD component</div>
                <div className="text-base font-semibold text-slate-700 mt-0.5" title={formatINR(outlook5y.npd_component || 0)}>{formatINRCompact(outlook5y.npd_component || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Assembly component</div>
                <div className="text-base font-semibold text-slate-700 mt-0.5" title={formatINR(outlook5y.assembly_component || 0)}>{formatINRCompact(outlook5y.assembly_component || 0)}</div>
              </div>
            </div>
          </div>
          <Link
            to={`/npd?red_flag=yes${unitFilter ? `&unit_id=${unitFilter}` : ""}`}
            className="bg-gradient-to-br from-rose-50/70 to-white border border-rose-100 rounded-xl p-3 hover:border-rose-300 hover:shadow-sm transition cursor-pointer block"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-sm text-slate-900">Margin Red Flags</h3>
                <p className="text-xs text-slate-500">NPDs below threshold</p>
              </div>
              <AlertTriangle size={18} className={kpi.red_flags > 0 ? "text-red-500" : "text-slate-400"} />
            </div>
            <div className={`text-2xl font-bold ${kpi.red_flags > 0 ? "text-red-600" : "text-slate-900"}`}>{kpi.red_flags ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">Review these before approval</div>
          </Link>
        </div>
      )}

      {!hideMargin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3" data-testid="business-quantum-row">
          <Link
            to={`/npd?awarded=no${unitFilter ? `&unit_id=${unitFilter}` : ""}`}
            className="bg-gradient-to-br from-sky-50/70 to-white border border-sky-100 rounded-xl p-3 hover:border-sky-300 hover:shadow-sm transition cursor-pointer block"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-sm text-slate-900">Potential Business Quantum · {activeLabel}</h3>
                <p className="text-xs text-slate-500">Up-for-grabs: sum of total project value across NPDs NOT yet awarded</p>
              </div>
              <TrendingUp size={18} className="text-sky-500" />
            </div>
            <div className="text-xl font-bold text-sky-700" data-testid="potential-quantum" title={formatINR(kpi.potential_business_quantum)}>{formatINRCompact(kpi.potential_business_quantum)}</div>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              Based on NPDs with RFQ-stage volumes captured. NPDs without volumes contribute ₹0.
            </div>
          </Link>
          <Link
            to={`/npd?awarded=yes${unitFilter ? `&unit_id=${unitFilter}` : ""}`}
            className="bg-gradient-to-br from-amber-50/70 to-white border border-amber-100 rounded-xl p-3 hover:border-amber-300 hover:shadow-sm transition cursor-pointer block"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-sm text-slate-900">Awarded Business Quantum · {activeLabel}</h3>
                <p className="text-xs text-slate-500">Confirmed: sum of total project value across {kpi.awarded_count ?? 0} awarded NPDs</p>
              </div>
              <Award size={18} className="text-amber-600" />
            </div>
            <div className="text-xl font-bold text-amber-700" data-testid="awarded-quantum" title={formatINR(kpi.awarded_business_quantum)}>{formatINRCompact(kpi.awarded_business_quantum)}</div>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              {kpi.awarded_count > 0 ? `${kpi.awarded_count} NPD${kpi.awarded_count !== 1 ? "s" : ""} awarded` : "No NPDs awarded yet"}
            </div>
          </Link>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 mb-3 ${showUnitTable ? "lg:grid-cols-[1.4fr_1fr_1fr] items-start" : "lg:grid-cols-2 items-stretch"}`}>
      {showUnitTable && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="dashboard-units-breakdown">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Building2 size={16} className="text-slate-400" />
            <h3 className="font-semibold text-sm text-slate-900">Per-Unit Breakdown</h3>
            <span className="text-xs text-slate-500">· Click a row to filter dashboard to that unit</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-right p-3">Draft</th>
                  <th className="text-right p-3">Negotiation</th>
                  <th className="text-right p-3">Approved</th>
                  <th className="text-right p-3">Awarded #</th>
                  <th className="text-right p-3">Potential ₹</th>
                  <th className="text-right p-3">Awarded ₹</th>
                  <th className="text-right p-3">Red Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" style={{ fontVariantNumeric: "tabular-nums" }}>
                {breakdown.map((u) => (
                  <tr
                    key={u.unit_id}
                    onClick={() => setUnitFilter(unitFilter === u.unit_id ? "" : u.unit_id)}
                    className={`cursor-pointer hover:bg-slate-50 transition-colors ${unitFilter === u.unit_id ? "bg-orange-50" : ""}`}
                    data-testid={`breakdown-row-${u.unit_code}`}
                  >
                    <td className="p-3">
                      <span className="font-mono font-semibold">{u.unit_code}</span>
                      <span className="text-slate-500 ml-2">{u.unit_name}</span>
                      {u.is_default && <span className="ml-2 text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">DEFAULT</span>}
                    </td>
                    <td className="p-3 text-right">{u.total}</td>
                    <td className="p-3 text-right text-slate-500">{u.draft}</td>
                    <td className="p-3 text-right text-sky-700">{u.in_negotiation}</td>
                    <td className="p-3 text-right text-emerald-700">{u.approved}</td>
                    <td className="p-3 text-right text-amber-700">{u.awarded_count || 0}</td>
                    <td className="p-3 text-right text-sky-700" title={formatINR(u.potential_business_quantum)}>{formatINRCompact(u.potential_business_quantum)}</td>
                    <td className="p-3 text-right text-amber-700" title={formatINR(u.awarded_business_quantum)}>{formatINRCompact(u.awarded_business_quantum)}</td>
                    <td className={`p-3 text-right ${u.red_flags > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                      {u.red_flags}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-semibold">
                  <td className="p-3 font-mono">ALL</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + u.total, 0)}</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + u.draft, 0)}</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + u.in_negotiation, 0)}</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + u.approved, 0)}</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + (u.awarded_count || 0), 0)}</td>
                  <td className="p-3 text-right">{formatINRCompact(breakdown.reduce((s, u) => s + (u.potential_business_quantum || 0), 0))}</td>
                  <td className="p-3 text-right">{formatINRCompact(breakdown.reduce((s, u) => s + (u.awarded_business_quantum || 0), 0))}</td>
                  <td className="p-3 text-right">{breakdown.reduce((s, u) => s + u.red_flags, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <h3 className="font-semibold text-sm text-slate-900 flex items-center gap-2"><span className="w-6 h-6 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><FileText size={13} /></span>Recent NPDs · {activeLabel}</h3>
            <Link to="/npd" className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">View all <ArrowRight size={13} /></Link>
          </div>
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
          ) : recent.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((n) => (
                <Link key={n.npd_id} to={`/npd/${n.npd_id}`} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-semibold text-slate-900 truncate">{n.part_name}</div>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_COLOR[n.status] || STATUS_COLOR.draft}`}>
                        {(n.status || "draft").toUpperCase()}
                      </span>
                      {n.approved_for_export && <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />}
                      {isAdmin && n.unit_code && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 font-mono px-1.5 py-0.5 rounded">{n.unit_code}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {n.npd_number} · {n.part_number} · {n.customer_name}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="text-sm font-semibold text-slate-900">{formatINR(n.grand_total)}</div>
                    {!hideMargin && n.margin_pct !== null && n.margin_pct !== undefined && (
                      <div className={`text-xs ${n.margin_red_flag ? "text-red-600" : "text-slate-500"}`}>
                        {n.margin_pct?.toFixed(2)}%
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {!hideMargin && top.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><Users size={13} /></span>
                <h3 className="font-semibold text-sm text-slate-900">Top Customers · {activeLabel}</h3>
              </div>
              <div className="space-y-1.5">
                {top.map((c, i) => {
                  const palette = ["bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700"];
                  const av = palette[i % palette.length];
                  return (
                    <div key={c.name} className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${av}`}>{(c.name || "?").slice(0, 2).toUpperCase()}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate text-xs">{c.name}</div>
                        <div className="text-[10px] text-slate-500">{c.count} NPD{c.count !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="text-right text-[11px] font-semibold text-slate-700" title={formatINR(c.pipeline_value)}>{formatINRCompact(c.pipeline_value)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>

      {/* ---- Assembly Pipeline Section (Round 15) ---- */}
      <div className="mt-10 mb-3 pt-6 border-t-2 border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <Layers size={20} className="text-orange-600" />
          <h2 className="text-lg font-bold text-slate-900">Assembly Pipeline · {activeLabel}</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Multi-part assemblies — independent stream. NPDs that are linked as components to any non-deleted assembly are automatically excluded from the NPD pipeline above to prevent double-counting. If you need a part to be counted in BOTH streams, keep it standalone (don't add it as an assembly component).
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <TintTile icon={Layers}       tint="orange"  label={`Total Assemblies · ${activeLabel}`} value={asmKpi.total} loading={loading} caption="Live multi-part builds" />
        <TintTile icon={CheckCircle2} tint="emerald" label="Approved"                            value={asmKpi.approved} loading={loading} caption="Cleared for export" />
        <TintTile icon={Award}        tint="amber"   label="Awarded"                             value={asmKpi.awarded_count} loading={loading} caption="Confirmed by customer" />
        <TintTile icon={Package}      tint="sky"     label="Components Used"                     value={null} loading={loading} caption="Sub-parts across assemblies" />
      </div>

      {!hideMargin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Potential Assembly Business · {activeLabel}</h3>
                <p className="text-xs text-slate-500">Sum of total project value across assemblies NOT yet awarded</p>
              </div>
              <TrendingUp size={18} className="text-sky-500" />
            </div>
            <div className="text-xl font-bold text-sky-700" title={formatINR(asmKpi.potential_business_quantum)}>{formatINRCompact(asmKpi.potential_business_quantum)}</div>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              Requires assembly volumes captured. Assemblies without YoY/volumes contribute ₹0.
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Awarded Assembly Business · {activeLabel}</h3>
                <p className="text-xs text-slate-500">Confirmed: total project value across {asmKpi.awarded_count ?? 0} awarded assemblies</p>
              </div>
              <Award size={18} className="text-amber-600" />
            </div>
            <div className="text-xl font-bold text-amber-700" title={formatINR(asmKpi.awarded_business_quantum)}>{formatINRCompact(asmKpi.awarded_business_quantum)}</div>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              {asmKpi.awarded_count > 0 ? `${asmKpi.awarded_count} assembl${asmKpi.awarded_count !== 1 ? "ies" : "y"} awarded` : "No assemblies awarded yet"}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3 items-start">
      {isAdmin && !unitFilter && asmBreakdown.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="assembly-units-breakdown">
          <div className="p-3 border-b border-slate-100 flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0"><Building2 size={13} /></span>
            <h3 className="font-semibold text-sm text-slate-900">Per-Unit Assembly Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-right p-3">Total ASM</th>
                  <th className="text-right p-3">Approved</th>
                  <th className="text-right p-3">Awarded #</th>
                  <th className="text-right p-3">Potential ₹</th>
                  <th className="text-right p-3">Awarded ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" style={{ fontVariantNumeric: "tabular-nums" }}>
                {asmBreakdown.map((u) => (
                  <tr
                    key={u.unit_id}
                    onClick={() => setUnitFilter(unitFilter === u.unit_id ? "" : u.unit_id)}
                    className={`cursor-pointer hover:bg-slate-50 transition-colors ${unitFilter === u.unit_id ? "bg-orange-50" : ""}`}
                    data-testid={`asm-breakdown-row-${u.unit_code}`}
                  >
                    <td className="p-3">
                      <span className="font-mono font-semibold">{u.unit_code}</span>
                      <span className="text-slate-500 ml-2">{u.unit_name}</span>
                      {u.is_default && <span className="ml-2 text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">DEFAULT</span>}
                    </td>
                    <td className="p-3 text-right">{u.total}</td>
                    <td className="p-3 text-right text-emerald-700">{u.approved}</td>
                    <td className="p-3 text-right text-amber-700">{u.awarded_count}</td>
                    <td className="p-3 text-right text-sky-700" title={formatINR(u.potential_business_quantum)}>{formatINRCompact(u.potential_business_quantum)}</td>
                    <td className="p-3 text-right text-amber-700" title={formatINR(u.awarded_business_quantum)}>{formatINRCompact(u.awarded_business_quantum)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-semibold">
                  <td className="p-3 font-mono">ALL</td>
                  <td className="p-3 text-right">{asmBreakdown.reduce((s, u) => s + u.total, 0)}</td>
                  <td className="p-3 text-right">{asmBreakdown.reduce((s, u) => s + u.approved, 0)}</td>
                  <td className="p-3 text-right">{asmBreakdown.reduce((s, u) => s + u.awarded_count, 0)}</td>
                  <td className="p-3 text-right">{formatINRCompact(asmBreakdown.reduce((s, u) => s + (u.potential_business_quantum || 0), 0))}</td>
                  <td className="p-3 text-right">{formatINRCompact(asmBreakdown.reduce((s, u) => s + (u.awarded_business_quantum || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recentAsm.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <h3 className="font-semibold text-sm text-slate-900 flex items-center gap-2"><span className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0"><Layers size={13} /></span>Recent Assemblies · {activeLabel}</h3>
            <Link to="/assemblies" className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">
              View all <ArrowRight size={13} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentAsm.map((a) => (
              <Link key={a.assembly_id} to={`/assemblies/${a.assembly_id}`} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-semibold text-slate-900 truncate">{a.assembly_name}</div>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                      {(a.status || "draft").toUpperCase()}
                    </span>
                    {a.approved_for_export && <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />}
                    {a.is_awarded && <Award size={12} className="text-amber-600 flex-shrink-0" />}
                    {isAdmin && a.unit_code && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 font-mono px-1.5 py-0.5 rounded">{a.unit_code}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.assembly_number} · {a.part_number} · {a.customer_name} · {a.components_count} sub-part{a.components_count !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-xs font-semibold text-slate-900">{formatINR(a.grand_total)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Maintenance-dashboard KPI card palette: a coloured LEFT accent + a soft tinted icon
// square on a clean white card. The big number stays dark slate for that crisp look.
const TINTS = {
  orange:  { accent: "border-l-orange-500",  chip: "bg-orange-50 text-orange-600" },
  amber:   { accent: "border-l-amber-500",   chip: "bg-amber-50 text-amber-600" },
  sky:     { accent: "border-l-sky-500",     chip: "bg-sky-50 text-sky-600" },
  emerald: { accent: "border-l-emerald-500", chip: "bg-emerald-50 text-emerald-600" },
  violet:  { accent: "border-l-violet-500",  chip: "bg-violet-50 text-violet-600" },
  rose:    { accent: "border-l-rose-500",    chip: "bg-rose-50 text-rose-600" },
  teal:    { accent: "border-l-teal-500",    chip: "bg-teal-50 text-teal-600" },
  indigo:  { accent: "border-l-indigo-500",  chip: "bg-indigo-50 text-indigo-600" },
};

// Scope pill (FY / month), matching the maintenance header chips.
function Pill({ children, active }) {
  return (
    <span className={`text-[11px] font-semibold px-2 py-1 rounded-md border whitespace-nowrap ${
      active ? "bg-sky-50 border-sky-300 text-sky-700" : "bg-slate-50 border-slate-200 text-slate-600"
    }`}>{children}</span>
  );
}

// Small uppercase section header with a leading icon (e.g. "PIPELINE SNAPSHOT").
function SectionLabel({ icon: Icon, children, right }) {
  return (
    <div className="flex items-center justify-between mb-2 mt-0.5">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {Icon && <Icon size={13} className="text-slate-400" />}{children}
      </div>
      {right && <div className="text-[11px] text-slate-400 font-medium">{right}</div>}
    </div>
  );
}

function TintTile({ icon: Icon, tint, label, value, caption, loading, to }) {
  const t = TINTS[tint] || TINTS.orange;
  const shown = loading || value === null || value === undefined ? "—" : value;
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${t.chip}`}><Icon size={17} /></div>
        {to && <ArrowRight size={13} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition" />}
      </div>
      <div className="mt-2 text-2xl font-extrabold tracking-tight tabular-nums text-slate-900">{shown}</div>
      <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mt-0.5">{label}</div>
      {caption && <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{caption}</div>}
    </>
  );
  const cls = `group bg-white border border-slate-200 ${t.accent} border-l-4 rounded-xl p-2.5 transition-all duration-200 block hover:-translate-y-0.5 hover:shadow-md`;
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

// Compact panel (maintenance-style): small icon + uppercase title header + optional right badge.
function Panel({ icon: Icon, title, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${className}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          {Icon && <Icon size={13} className="text-slate-400" />}{title}
        </div>
        {right}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

// Colour-on-white donut for panels (the gradient Donut above draws white-on-colour).
function MiniDonut({ pct, color = "#10b981", size = 88, stroke = 11, label, sub }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
          {p > 0 && (
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
              strokeDasharray={`${(p / 100) * circ} ${circ}`} strokeLinecap="round" />
          )}
        </g>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-extrabold text-slate-900 tabular-nums" style={{ fontSize: Math.round(size * 0.22) }}>{label}</span>
        {sub && <span className="text-slate-400 mt-0.5" style={{ fontSize: Math.round(size * 0.13) }}>{sub}</span>}
      </div>
    </div>
  );
}

// One status row: colour dot + label + thin bar + count.
function StatusBar({ color, label, value, total }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`w-2 h-2 rounded-sm ${color}`} aria-hidden="true" />
      <span className="w-20 shrink-0 text-slate-600">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      <span className="w-7 text-right font-bold text-slate-800 tabular-nums">{value}</span>
    </div>
  );
}

// Horizontal bar row (department-style): label + bar + value + percent.
function BarRow({ label, value, pct }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-24 shrink-0 truncate font-mono text-slate-600" title={label}>{label}</span>
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      <span className="w-8 text-right font-bold text-slate-800 tabular-nums">{value}</span>
      <span className="w-9 text-right text-slate-400 tabular-nums">{Math.round(pct)}%</span>
    </div>
  );
}

// Label + sub on the left, big number on the right.
function MiniStat({ label, value, sub, color = "text-slate-900", title }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[11px] text-slate-600">{label}</div>
        {sub && <div className="text-[10px] text-slate-400 truncate">{sub}</div>}
      </div>
      <div className={`text-base font-extrabold tabular-nums shrink-0 ${color}`} title={title}>{value}</div>
    </div>
  );
}

// Meter: fill = white, track = a lighter step of the same (white) ramp.
function Donut({ pct, label, sub, size = 104, stroke = 10 }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${p.toFixed(1)} percent ${sub || ""}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth={stroke} />
          {p > 0 && (
            <circle
              cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ffffff" strokeWidth={stroke}
              strokeDasharray={`${(p / 100) * circ} ${circ}`} strokeLinecap="round"
            />
          )}
        </g>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none px-1">
        <span className="font-bold leading-none tabular-nums" style={{ fontSize: Math.max(11, Math.round(size * 0.24)) }}>{label}</span>
        {sub && <span className="text-white/75 leading-none mt-0.5" style={{ fontSize: Math.max(8, Math.round(size * 0.15)) }}>{sub}</span>}
      </div>
    </div>
  );
}

// 6-point trend line, drawn in the card's own ink (white on the gradient).
function Sparkline({ points = [], w = 150, h = 28 }) {
  const valid = points.filter((p) => p != null);
  if (valid.length < 2) {
    return <div className="text-[10px] text-white/60 h-[28px] flex items-center">6-month trend · not enough data</div>;
  }
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = Math.max(max - min, 1);
  const pad = 3;
  const xStep = (w - pad * 2) / Math.max(points.length - 1, 1);
  const y = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  const xy = points.map((v, i) => (v == null ? null : [pad + i * xStep, y(v)]));
  const path = xy.filter(Boolean).map(([x, yy], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${yy.toFixed(1)}`).join(" ");
  const last = [...xy].reverse().find(Boolean);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {last && <circle cx={last[0]} cy={last[1]} r="3.5" fill="#ffffff" />}
    </svg>
  );
}

// Ordered composition (draft → negotiation → approved) as one white ramp.
function CompositionBar({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return null;
  return (
    <div className="mt-4">
      <div className="flex h-2 rounded-full overflow-hidden gap-[2px]">
        {segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.label}
              style={{ width: `${(s.value / total) * 100}%`, backgroundColor: `rgba(255,255,255,${s.opacity})` }}
              title={`${s.label}: ${s.value}`}
            />
          ) : null
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 text-[10px] text-white/85">
        {segments.map((s) => (
          <span key={s.label} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: `rgba(255,255,255,${s.opacity})` }} aria-hidden="true" />
            {s.label} <strong className="text-white">{s.value}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function RolePerm({ allowed, label }) {
  return (
    <div className="flex items-center gap-2">
      {allowed
        ? <CheckCircle2 size={14} className="text-emerald-500" />
        : <AlertTriangle size={14} className="text-slate-300" />}
      <span className={allowed ? "text-slate-700" : "text-slate-400 line-through"}>{label}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 mx-auto bg-orange-50 rounded-full flex items-center justify-center text-orange-600 mb-3">
        <FileText size={20} />
      </div>
      <p className="text-sm font-medium text-slate-700">No NPDs in this view</p>
      <p className="text-xs text-slate-500 mt-1">Try switching to "All Units" or create a new costing.</p>
      <Link to="/npd/new" className="inline-block mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium">Create NPD →</Link>
    </div>
  );
}
